import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import SendIcon from "@mui/icons-material/Send";
import { Box, ClickAwayListener, IconButton, InputBase, List, ListItemButton, ListItemText, Portal, Tooltip, Typography } from "@mui/material";
import { colors } from "../../../../theme/muiColors";
import { useEffect, useRef, useState } from "react";
import type { MessageGroupSummary } from "../../../message-groups/messageGroups.types";

interface SendNewGroupMessageProps {
  groups: MessageGroupSummary[];
  canCreateGroups: boolean;
  onCreateGroup: () => void;
  onCreateSmsGroup: () => void;
  onSelectGroup: (group: MessageGroupSummary) => void;
}

const getGroupSearchText = (group: MessageGroupSummary) =>
  [
    group.name,
    group.description,
    ...group.members.map((member) => `${member.firstName} ${member.lastName}`),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const SendNewGroupMessage = ({
  groups,
  canCreateGroups,
  onCreateGroup,
  onCreateSmsGroup,
  onSelectGroup,
}: SendNewGroupMessageProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const filteredGroups = groups.filter((group) => {
    if (!searchTerm) return false;
    return getGroupSearchText(group).includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open, searchTerm]);

  const handleSelectGroup = (group: MessageGroupSummary) => {
    onSelectGroup(group);
    setSearchTerm("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && filteredGroups.length === 1) {
      e.preventDefault();
      handleSelectGroup(filteredGroups[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box>
          <Box
            ref={anchorRef}
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 1.5,
              px: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: colors.blue[500],
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              To
            </Typography>

            <InputBase
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                fontSize: 13,
                color: "var(--fx-surface-textsecondary, #64748B)",
                "& input": { p: 0 },
                "& input::placeholder": {
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  opacity: 1,
                },
              }}
            />

            <IconButton
              type="button"
              size="small"
              disabled={filteredGroups.length !== 1}
              disableRipple
              onClick={() => filteredGroups.length === 1 && handleSelectGroup(filteredGroups[0])}
              sx={{
                p: 0.5,
                flexShrink: 0,
                transition: "opacity 0.15s",
                opacity: filteredGroups.length === 1 ? 1 : 0.25,
                color: colors.blue[500],
                "&:hover": {
                  bgcolor: "transparent",
                  color: colors.blue[300],
                },
                "&.Mui-disabled": {
                  color: colors.blue[300],
                },
              }}
            >
              <SendIcon sx={{ fontSize: 15 }} />
            </IconButton>

            {canCreateGroups && (
              <>
                <Tooltip title="New group" arrow>
                  <IconButton size="small" aria-label="New group" onClick={onCreateGroup} sx={{ p: 0.5, color: colors.blue[500], flexShrink: 0 }}>
                    <PersonAddAlt1Icon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="New group text" arrow>
                  <IconButton
                    size="small"
                    aria-label="New group text"
                    onClick={onCreateSmsGroup}
                    sx={{ p: 0.5, color: colors.orange[500], flexShrink: 0 }}
                  >
                    <SmsRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          {open && searchTerm.length > 0 && (
            <Portal>
              <Box
                sx={{
                  position: "fixed",
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                  zIndex: 2147483647,
                  maxHeight: 240,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "divider",
                    borderRadius: "3px",
                  },
                }}
              >
                {filteredGroups.length > 0 ? (
                  <List disablePadding>
                    {filteredGroups.map((group) => (
                      <ListItemButton
                        key={group.groupUid}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectGroup(group);
                        }}
                        sx={{
                          py: 1.5,
                          "&:hover": {
                            bgcolor: colors.blue[50],
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{group.name}</Typography>
                          }
                          secondary={
                            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                              {group.membersCount} {group.membersCount === 1 ? "member" : "members"}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No groups found for "{searchTerm}"</Typography>
                  </Box>
                )}
              </Box>
            </Portal>
          )}
        </Box>
      </ClickAwayListener>
    </Box>
  );
};
